import classNames from "classnames";
import * as React from "react";
import { ChangeEvent } from "react";
import { Menu } from "react-aria-menubutton";
import { Trans, useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { toast } from "react-toastify";

import style from "./template-kebab-menu.scss";
import AlertDialog from "components/alert-dialog/AlertDialog";
import DeleteConfirmationDialog from "components/confirmation/DeleteConfirmationDialog";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { SuccessIcon } from "components/icons/SuccessIcon";
import kebabStyle from "components/kebab-menu/kebab-menu.scss";
import layout from "components/layout/layout.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import MenuItemButton from "components/menu-item-button/MenuItemButton";
import Modal from "components/modal/Modal";
import { UpdateDialogDetails } from "components/workflows/workflow-templates/WorkflowTemplateTable";
import { AUTH_WORKFLOW_TEMPLATE_DELETE, AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW } from "domain/authority";
import { ConnectedWorkflows } from "domain/workflows";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import { templateService } from "services/workflows/TemplateService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);
const WorkflowTemplateKebabMenu = (
    props: ConnectedProps<typeof connector> & {
        uuid: string;
        name: string;
        version: string;
        connectedWorkflows: ConnectedWorkflows;
        onDelete: () => void;
        onUpdate: () => void;
        isSubWorkflow: boolean;
        product: string;
        updateVisible: boolean;
        setUpdateDialogVisibility: React.Dispatch<React.SetStateAction<UpdateDialogDetails>>;
        setIsSidePanelOpen?: (isOpen: boolean) => void;
    }
): JSX.Element => {
    const { t } = useTranslation();
    const [deleteSubworkflowDialog, setDeleteSubworkflowDialog] = React.useState(false);
    const [errorDialog, setErrorDialog] = React.useState(false);
    const [deleteTemplateModalVisible, setDeleteTemplateModalVisible] = React.useState(false);

    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [okClicked, setOkClicked] = React.useState(false);
    React.useEffect(() => {
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, []);

    const theme = useTheme();
    const [disabled, setDisabled] = React.useState(true);

    const exportTemplate = () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        templateService.export(props.uuid, abortController);
    };

    const deleteTemplate = () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        templateService
            .deleteTemplate(props.uuid, abortController)
            .then(() => {
                setOkClicked(false);
                setDeleteTemplateModalVisible(false);
                toast(
                    <SuccessIcon
                        successClass={layout.customToastSuccessIcon}
                        color={theme.contentBackgroundColor}
                        text={
                            props.isSubWorkflow
                                ? t("workflowTemplatesManager.deleteSubWorkflow.successMessage", { name: props.name })
                                : t("workflowTemplatesManager.deleteTemplate.successMessage", { name: props.name })
                        }
                    />,
                    {
                        closeButton: (closeToastProps) => (
                            <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                        ),
                        className: layout.customToastSuccessMessage,
                    }
                );
                props.onDelete();
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setDeleteTemplateModalVisible(false);
                    setErrorDialog(true);
                    setOkClicked(false);
                }
            });
    };
    const handleTemplateDelete = () => {
        setOkClicked(true);
        deleteTemplate();
    };

    const handleConfirmation = (event: ChangeEvent<HTMLInputElement>) => {
        setDisabled(event.target.value !== t("Common.delete").toUpperCase());
    };

    const handleUpdate = () => {
        props.setUpdateDialogVisibility({
            visible: true,
            uuid: props.uuid,
            editorVersion: props.version,
            profile: props.product,
            name: props.name,
        });
    };

    return (
        <div className={style.templatesIcon}>
            <Menu className={kebabStyle.kebabMenuItem}>
                <ul>
                    {props.updateVisible && (
                        <li className={kebabStyle.listItem}>
                            <MenuItemButton
                                className={kebabStyle.menuItem}
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.WORKFLOW_TEMPLATES,
                                        action: Action.UPDATE_WORKFLOW_TEMPLATE_INITIATED,
                                    });
                                    handleUpdate();
                                }}
                                data-testid={
                                    testIds.workArea.workflowTemplatesManager.primaryView.table.kebabMenu.updateButton
                                }
                            >
                                {t("Common.update")}
                            </MenuItemButton>
                        </li>
                    )}
                    {userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_TEMPLATE_MANAGEMENT_VIEW]) && (
                        <li className={kebabStyle.listItem}>
                            <MenuItemButton
                                className={kebabStyle.menuItem}
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.WORKFLOW_TEMPLATES,
                                        action: Action.EXPORT_WORKFLOW_TEMPLATE,
                                    });
                                    exportTemplate();
                                }}
                                data-testid={
                                    testIds.workArea.workflowTemplatesManager.primaryView.table.kebabMenu.exportButton
                                }
                            >
                                {t("workflowTemplatesManager.template.tooltipExport")}
                            </MenuItemButton>
                        </li>
                    )}
                    {userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_TEMPLATE_DELETE]) && (
                        <li className={kebabStyle.listItem}>
                            <MenuItemButton
                                className={kebabStyle.menuItem}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (props.setIsSidePanelOpen) {
                                        props.setIsSidePanelOpen(false);
                                    }
                                    usageStatisticsService.sendEvent({
                                        category: Category.WORKFLOW_TEMPLATES,
                                        action: Action.REMOVE_WORKFLOW_TEMPLATE,
                                    });

                                    props.connectedWorkflows.parentWorkflows.length
                                        ? setDeleteSubworkflowDialog(true)
                                        : setDeleteTemplateModalVisible(true);
                                }}
                                data-testid={testIds.workArea.workflows.table.kebabMenu.deleteButton}
                            >
                                <div className={kebabStyle.deleteText}>{t("Common.delete")}</div>
                            </MenuItemButton>
                        </li>
                    )}
                </ul>
            </Menu>

            <Modal
                isOpen={deleteSubworkflowDialog}
                hideModal={() => {
                    setDeleteSubworkflowDialog(false);
                }}
                modalTitle={t("workflowTemplatesManager.deleteSubWorkflow.title")}
            >
                {okClicked ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <div className={form.deleteContainer}>
                            <Trans
                                i18nKey={"workflowTemplatesManager.subWorkflows.subworkflowIntroductionMessage"}
                                values={{
                                    name: props.name,
                                }}
                                components={[<strong key={"tenantType"} />]}
                            />
                        </div>
                        <div className={form.deleteContainer}>
                            {t("workflowTemplatesManager.subWorkflows.subworkflowAssociatedWith")}
                            <div className={style.listBox}>
                                <ul>
                                    {props.connectedWorkflows.parentWorkflows.map((each) => (
                                        <li key={each}>{each}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className={form.deleteContainer}>
                            {t("workflowTemplatesManager.subWorkflows.subworkflowIntroductionMessagePost", {
                                name: props.name,
                            })}
                        </div>

                        <div className={form.deleteContainer}>
                            {t("workflowTemplatesManager.subWorkflows.confirmationMessage")}
                        </div>

                        <div className={form.deleteContainer}>
                            <input
                                id="confirmInput"
                                type="text"
                                data-testid={testIds.common.confirmationDialog.confirmInput}
                                onChange={(event) => handleConfirmation(event)}
                                className={form.input}
                            />
                        </div>
                        <div className={classNames(buttons.okButtonContainer, form.buttonContainer)}>
                            <button
                                className={buttons.secondaryButtonWithoutIcon}
                                onClick={() => {
                                    setDisabled(true);
                                    setDeleteSubworkflowDialog(false);
                                }}
                                data-testid={testIds.common.dialog.closeButton}
                            >
                                {t("Common.cancel")}
                            </button>

                            <button
                                className={classNames(buttons.deleteButton, buttons.primaryButtonWithoutIcon)}
                                data-testid={testIds.common.confirmationDialog.confirmButton}
                                onClick={() => {
                                    setDisabled(true);
                                    handleTemplateDelete();
                                }}
                                disabled={disabled}
                            >
                                {t("Common.delete")}
                            </button>
                        </div>
                    </>
                )}
            </Modal>
            <DeleteConfirmationDialog
                modalDisplayed={deleteTemplateModalVisible}
                introductionMessage={
                    <div>
                        <p>
                            {t("workflowTemplatesManager.deleteTemplate.introductionMessagePre")}
                            <b>{props.name}</b>
                            {t("workflowTemplatesManager.deleteTemplate.introductionMessagePost")}
                        </p>
                    </div>
                }
                confirmationMessage={t("workflowTemplatesManager.deleteTemplate.confirmationMessage")}
                handleCommand={handleTemplateDelete}
                setModalDisplayed={() => setDeleteTemplateModalVisible(false)}
                title={
                    props.isSubWorkflow
                        ? t("workflowTemplatesManager.deleteSubWorkflow.title")
                        : t("workflowTemplatesManager.deleteTemplate.title")
                }
                loading={okClicked}
            />
            <AlertDialog
                isOpen={errorDialog}
                hideModal={() => {
                    setErrorDialog(false);
                }}
                type={"ERROR"}
                headingText={
                    props.isSubWorkflow
                        ? t("workflowTemplatesManager.subWorkflows.failureTitle")
                        : t("workflowTemplatesManager.deleteTemplate.failureTitle")
                }
                primaryButton={{
                    text: t("Common.deleteAgain"),
                    action: () => {
                        setErrorDialog(false);
                        setDeleteTemplateModalVisible(true);
                    },
                }}
                closeButton={{
                    action: () => {
                        setErrorDialog(false);
                    },
                    text: t("Common.cancel"),
                }}
            />
        </div>
    );
};

export default connector(WorkflowTemplateKebabMenu);
